import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import type { SortOrder } from '@/types/ltv';

// Receita vem do GAM em DÓLAR (USD) — por isso US$, não R$.
export const money = (v?: number) =>
    'US$ ' + (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const num = (v?: number) => (v ?? 0).toLocaleString('pt-BR');
export const dec = (v?: number) =>
    (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Delta % com sinal, ex: +8,7% / -2,3%
export const pct = (v?: number) => {
    const n = v ?? 0;
    const s = Math.abs(n).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return `${n > 0 ? '+' : n < 0 ? '−' : ''}${s}%`;
};

// Nome do país a partir do código do lead_pais (ex: "PL_PL" -> "Polônia").
// Usa Intl.DisplayNames (pt-BR). Cai de volta pro valor cru se não reconhecer.
const REGION_NAMES = (() => {
    try { return new Intl.DisplayNames(['pt-BR'], { type: 'region' }); } catch { return null; }
})();
export const countryName = (raw?: string | null): string => {
    if (!raw) return '—';
    const v = String(raw).trim();
    const parts = v.split(/[_\-\s/]+/).filter(Boolean);
    const code = (parts[parts.length - 1] || v).toUpperCase().slice(0, 2);
    if (REGION_NAMES && /^[A-Z]{2}$/.test(code)) {
        try {
            const name = REGION_NAMES.of(code);
            if (name && name.toUpperCase() !== code) return name;
        } catch { /* ignora */ }
    }
    return v;
};

// Iniciais pro avatar (ex: "Alice de Matos" -> "AM")
export const initials = (name?: string | null) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '—';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Cabeçalho ordenável — clica pra ordenar/alternar asc-desc
export function SortHeader({
    label, field, sortBy, sortOrder, onSort, align = 'left',
}: {
    label: string;
    field: string;
    sortBy: string;
    sortOrder: SortOrder;
    onSort: (field: string) => void;
    align?: 'left' | 'right';
}) {
    const active = sortBy === field;
    return (
        <th
            onClick={() => onSort(field)}
            className={`px-4 py-3 text-${align} text-[11px] font-semibold text-gray-400 uppercase tracking-wide cursor-pointer select-none hover:text-gray-600 whitespace-nowrap`}
        >
            <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
                {label}
                {active
                    ? (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />)
                    : <ChevronsUpDown className="w-3 h-3 opacity-30" />}
            </span>
        </th>
    );
}

// Toggle de ordenação: mesmo campo alterna asc/desc; campo novo começa desc
export function toggleSort(field: string, sortBy: string, sortOrder: SortOrder): { sort_by: string; sort_order: SortOrder } {
    if (field === sortBy) return { sort_by: field, sort_order: sortOrder === 'asc' ? 'desc' : 'asc' };
    return { sort_by: field, sort_order: 'desc' };
}

export function Spinner({ label = 'Carregando...' }: { label?: string }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-12 flex items-center justify-center gap-3 text-gray-400">
            <div className="w-4 h-4 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
            <span className="text-sm">{label}</span>
        </div>
    );
}

export function Empty({ label = 'Nenhum resultado.' }: { label?: string }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-sm text-gray-400">
            {label}
        </div>
    );
}

// Paginação por offset
export function Pagination({
    total, limit, offset, onOffset,
}: {
    total: number;
    limit: number;
    offset: number;
    onOffset: (offset: number) => void;
}) {
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const currentPage = Math.floor(offset / limit) + 1;
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-gray-400">
                {total.toLocaleString('pt-BR')} itens · página {currentPage} de {totalPages}
            </p>
            <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={currentPage === 1}
                    onClick={() => onOffset((currentPage - 2) * limit)} className="w-8 h-8 p-0 rounded-lg">
                    <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pg = currentPage <= 3 ? i + 1 : currentPage >= totalPages - 2 ? totalPages - 4 + i : currentPage - 2 + i;
                    if (pg < 1 || pg > totalPages) return null;
                    return (
                        <Button key={pg} variant={pg === currentPage ? 'default' : 'outline'} size="sm"
                            onClick={() => onOffset((pg - 1) * limit)} className="w-8 h-8 p-0 rounded-lg text-xs">
                            {pg}
                        </Button>
                    );
                })}
                <Button variant="outline" size="sm" disabled={currentPage === totalPages}
                    onClick={() => onOffset(currentPage * limit)} className="w-8 h-8 p-0 rounded-lg">
                    <ChevronRight className="w-3.5 h-3.5" />
                </Button>
            </div>
        </div>
    );
}
